import {
  CalendarOutlined,
  CrownFilled,
  FireFilled,
  ProjectOutlined,
  RightOutlined,
  StarFilled,
  UnorderedListOutlined,
  TrophyFilled,
  TrophyOutlined,
} from "@ant-design/icons";
import { Card, Popover } from "antd";
import { ReactNode } from "react";
import MemberAvatar from "../MemberAvatar";
import "./index.css";

export interface WeeklyTaskRankingItem {
  user_id: string;
  nickname: string;
  avatar?: string;
  total: number;
  completed: number;
  pending: number;
}

export interface WeeklyTaskRanking {
  week_start: number;
  week_end: number;
  items: WeeklyTaskRankingItem[];
}

interface TaskLeaderboardProps {
  activeProjectCount: number;
  currentUserId: string;
  getTextAvatar: (text: string) => {
    initial: string;
    background: string;
  };
  onWeekPlanOpenChange: (open: boolean) => void;
  pendingTaskCount: number;
  ranking: WeeklyTaskRanking | null;
  weekPlanContent: ReactNode;
}

const RANK_LABELS = ["冠军", "亚军", "季军"];

function formatWeekRange(startTime?: number, endTime?: number) {
  if (!startTime || !endTime) return "本周";
  const start = new Date(startTime);
  const end = new Date(endTime - 1);
  const format = (date: Date) =>
    `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
      date.getDate()
    ).padStart(2, "0")}`;
  return `${format(start)} - ${format(end)}`;
}

export default function TaskLeaderboard({
  activeProjectCount,
  currentUserId,
  getTextAvatar,
  onWeekPlanOpenChange,
  pendingTaskCount,
  ranking,
  weekPlanContent,
}: TaskLeaderboardProps) {
  const items = ranking?.items || [];
  const renderRankingItem = (
    item: WeeklyTaskRankingItem,
    index: number
  ) => {
    const rank = index + 1;
    const featured = rank <= 3;
    const RankIcon = [CrownFilled, TrophyFilled, StarFilled][index];
    const completionRate = item.total
      ? Math.round((item.completed / item.total) * 100)
      : 0;
    const isCurrentUser = item.user_id === currentUserId;
    const displayName = item.nickname || item.user_id;

    return (
      <article
        aria-label={`第 ${rank} 名 ${displayName}`}
        className={[
          "rankingEntry",
          featured ? "rankingEntry--elite" : "rankingEntry--standard",
          `rankingEntry--rank${rank}`,
          isCurrentUser ? "rankingEntry--current" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        key={item.user_id}
        role="listitem"
      >
        {featured && (
          <div aria-hidden="true" className="rankingFlameAura">
            {Array.from({ length: rank === 1 ? 5 : 3 }).map((_, flameIndex) => (
              <FireFilled key={flameIndex} />
            ))}
          </div>
        )}
        <div className={featured ? "rankingMedal" : "rankingPosition"}>
          {featured && (
            <>
              <i className="rankingMedal__ribbon rankingMedal__ribbon--left" />
              <i className="rankingMedal__ribbon rankingMedal__ribbon--right" />
            </>
          )}
          <strong
            aria-label={
              RankIcon ? `${RANK_LABELS[index]}徽章` : `第 ${rank} 名`
            }
          >
            {RankIcon ? <RankIcon /> : rank}
          </strong>
          <span>{RANK_LABELS[index] || `第 ${rank} 名`}</span>
        </div>
        <MemberAvatar
          avatar={item.avatar}
          className="rankingEntry__avatar"
          displayName={displayName}
          fallback={getTextAvatar(displayName)}
          size={featured ? 52 : 42}
        />
        <div className="rankingEntry__identity">
          <div>
            <strong title={displayName}>{displayName}</strong>
            {isCurrentUser && <em>我</em>}
          </div>
          <span title={item.user_id}>{item.user_id}</span>
        </div>
        <div
          aria-label={`${displayName}完成率 ${completionRate}%`}
          className="rankingEntry__progress"
        >
          <div className="rankingEntry__progressMeta">
            <span>
              已完成 {item.completed} · 待完成 {item.pending}
            </span>
            <strong>{completionRate}%</strong>
          </div>
          <div className="rankingEntry__progressTrack">
            <i style={{ width: `${completionRate}%` }} />
          </div>
        </div>
        <div className="rankingEntry__total">
          <strong>{item.total}</strong>
          <span>项任务</span>
        </div>
      </article>
    );
  };

  return (
    <section className="weeklyDashboardGrid">
      <Card className="workspacePanel weeklyOverviewCard">
        <div className="workspacePanel__header weeklyOverviewCard__header">
          <div>
            <span className="taskLeaderboard__eyebrow">工作台</span>
            <h3>
              <CalendarOutlined />
              本周概览
            </h3>
            <p>集中查看本周计划与任务节奏</p>
          </div>
        </div>

        <div aria-label="本周工作台摘要" className="taskLeaderboard__summary">
          <Popover
            content={weekPlanContent}
            onOpenChange={onWeekPlanOpenChange}
            overlayClassName="weekPlanOverlay"
            placement="bottomLeft"
            trigger={["hover", "click"]}
          >
            <button
              className="taskLeaderboard__summaryItem taskLeaderboard__summaryItem--action"
              type="button"
            >
              <span className="taskLeaderboard__summaryIcon">
                <CalendarOutlined />
              </span>
              <span className="taskLeaderboard__summaryCopy">
                <strong>本周计划</strong>
                <small>查看每日任务安排</small>
              </span>
              <RightOutlined className="taskLeaderboard__summaryArrow" />
            </button>
          </Popover>
          <div className="taskLeaderboard__summaryItem">
            <span className="taskLeaderboard__summaryIcon">
              <ProjectOutlined />
            </span>
            <span className="taskLeaderboard__summaryMetric">
              <strong>{activeProjectCount}</strong>
              <small>进行中项目</small>
            </span>
          </div>
          <div className="taskLeaderboard__summaryItem">
            <span className="taskLeaderboard__summaryIcon">
              <UnorderedListOutlined />
            </span>
            <span className="taskLeaderboard__summaryMetric">
              <strong>{pendingTaskCount}</strong>
              <small>待处理任务</small>
            </span>
          </div>
        </div>
      </Card>

      <Card className="workspacePanel taskLeaderboard">
        <div className="workspacePanel__header taskLeaderboard__header">
          <div>
            <h3>
              <TrophyOutlined />
              本周任务排行榜
            </h3>
            <p>按负责人参与的任务内容项数量排名</p>
          </div>
          <span>{formatWeekRange(ranking?.week_start, ranking?.week_end)}</span>
        </div>

        {!ranking ? (
          <div className="taskLeaderboard__empty">正在同步本周榜单...</div>
        ) : items.length === 0 ? (
          <div className="taskLeaderboard__empty">
            本周暂时没有可参与排名的任务
          </div>
        ) : (
          <div className="taskLeaderboard__body">
            <div
              aria-label="本周任务排行榜前六名"
              className="rankingBoard"
              role="list"
            >
              <div className="rankingBoard__row rankingBoard__row--champion">
                {items[0] ? renderRankingItem(items[0], 0) : null}
              </div>
              <div className="rankingBoard__row rankingBoard__row--honors">
                {items
                  .slice(1, 3)
                  .map((item, index) => renderRankingItem(item, index + 1))}
              </div>
              <div className="rankingBoard__row rankingBoard__row--finalists">
                {items
                  .slice(3, 6)
                  .map((item, index) => renderRankingItem(item, index + 3))}
              </div>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}
